import {
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    LegacyCard,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    TextContainer,
    List,
    LegacyStack,
    Select,
    Text,
    Icon,
    Tooltip,
    Checkbox, InlineStack, Badge
} from "@shopify/polaris";
import {QuestionMarkMajor} from '@shopify/polaris-icons';
import {useCallback, useEffect, useState} from "react";
import {cloneDeep} from 'lodash';
import {useAppBridge, useToast} from "@shopify/app-bridge-react";
import {Redirect} from '@shopify/app-bridge/actions';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import Api from '../../helpers/api';
import debounce from 'lodash.debounce';
import CustomerOnHold from "../customers/CustomerOnHold";
import {getSessionToken} from "@shopify/app-bridge-utils";
import jwtDecode from "jwt-decode";
import * as helpers from './helpers';
import {filterShippingMethods, getErrorMessage} from "../../helpers/utils";
import parse from 'html-react-parser';
import MultipleSelect from "../MultipleSelect";

const AddressDetails = ({address}) => {
    const parts = [
        `${address?.company || ''}`,
        `${address?.address1 || ''}`,
        `${address?.address2 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip_code || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Defined)</span>)
}

const BillingDetails = ({address}) => {
    const parts = [
        `${address?.business_name || ''}`,
        `${address?.address_line_1 || ''}`,
        `${address?.address_line_2 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip_code || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Assigned)</span>)
}

const PriceMarker = ({price, strikethrough}) => {
    const style = strikethrough
        ? {textDecoration: 'line-through'}
        : {};

    return (
        <Text variant="headingMd" as="h2">
            <span style={style}>${price}</span>
        </Text>
    );
}

const FinalPrice = ({lineItem, children}) => {
    const [showInput, setShowInput] = useState(false);
    const onClick = useCallback(() => {
        if (!lineItem.price.locked) {
            setShowInput(true);
        }
    }, []);

    let discounted = lineItem.price.discounted || '0.00';

    if (lineItem.price.discount && lineItem.price.discount.quantity > 0 && discounted > 0) {
        let totalDiscount = lineItem.price.discount.total;
        discounted = discounted - parseFloat((totalDiscount / lineItem.price.discount.quantity).toFixed(2));
    }

    const prices = Array
        .from(new Set([
            parseFloat(lineItem.price.unit || '0.00').toFixed(2),
            parseFloat(discounted).toFixed(2),
        ]))
        .map((price, i, list) => ({
            price,
            strikethrough: i == 0 && list.length > 1,
        }));

    const priceBreaks = lineItem?.price_breaks || {};
    const priceBreakList = Object.entries(priceBreaks)
        .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
        .map(([qty, price]) => `≥ ${qty}: $${parseFloat(price).toFixed(2)}`)
        .join('\n');

    const hasPriceBreaks = Object.keys(priceBreaks).length > 0;

    if (!showInput) {
        const style = lineItem.price.locked ? {} : {cursor: 'pointer'};

        return (
            <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <div onClick={onClick} title="Enter custom price" style={style}>
                    {prices.map((props, i) => <PriceMarker {...props} key={i}/>)}
                </div>

                {hasPriceBreaks && (
                    <Tooltip content={<pre style={{margin: 0}}>{priceBreakList}</pre>}>
                        <Icon source={QuestionMarkMajor} tone="base"/>
                    </Tooltip>
                )}
            </div>
        );
    }

    return <>{children}</>;
};

const DiscountFootnote = ({discount}) => {
    const {total} = discount;

    return (total)
        ? (<span>Discount Code: ${total}</span>)
        : null;
}

const Orders = () => {
    const toast = useToast();
    const search = useLocation().search;
    const app = useAppBridge();
    const history = useHistory();
    const redirect = Redirect.create(app);
    const {customerid} = useParams();
    const [locationId] = useState(
        (new URLSearchParams(search)).get('location_id') || null
    );
    const [adminUserId, setAdminUserId] = useState(null);

    const [customer, setCustomer] = useState(null);
    const [location, setLocation] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [collections, setCollections] = useState([]);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [additionalInfo, setAdditionalInfo] = useState({
        placed_by: '',
        po_number: '',
        salesperson_number: '',
        order_notes: '',
        customer_notes: ''
    });
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setSearching] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [discountCode, setDiscountCode] = useState({
        code: '',
        requires_refresh: false,
        loading: false,
        applied: false,
        fetched_at: 0,
        id: null,
    });
    const [shippingMethods, setShippingMethods] = useState([]);
    const [shippingMethod, setShippingMethod] = useState(null);
    const [deletedBillingAddress, setDeletedBillingAddress] = useState(false);
    const [includeDrafts, setIncludeDrafts] = useState(false);
    const [includeArchived, setIncludeArchived] = useState(false);

    const [isCalculateVisible, setIsCalculateVisible] = useState(false);
    const [isPlaceOrderVisible, setIsPlaceOrderVisible] = useState(false);
    const [calculated, setCalculated] = useState(false);
    const [errMessage, setErrMessage] = useState(null);

    const applyDiscountCode = useCallback(() => {
        setCalculated(false);

        setDiscountCode(state => ({
            ...state,
            requires_refresh: false,
            loading: true,
            applied: true,
        }));
    }, []);

    const removeDiscountCode = useCallback(() => {
        setCalculated(false);

        setDiscountCode(state => ({
            ...state,
            code: '',
            requires_refresh: false,
            loading: true,
            applied: false,
        }));
    }, []);

    useEffect(() => {
        if (discountCode.loading) {
            setIsPlaceOrderVisible(false);
            setIsCalculateVisible(true);
            calculateOrder();
        }
    }, [discountCode.loading]);

    useEffect(() => {
        if (calculated) {
            return;
        }

        if (lineItems.some(item => parseInt(item.quantity) > 0)) {
            setIsCalculateVisible(true);
            setIsPlaceOrderVisible(false);
        } else {
            setIsCalculateVisible(false);
            setIsPlaceOrderVisible(false);
        }
    }, [lineItems, calculated]);

    useEffect(() => {
        searchProducts()
    }, [selectedCollections]);

    //This is trick that Rado provided, ask him, I don't know how it works :)
    //This function basically rounds up/down quantity to closest step
    const debounceQuantityUpdate = useCallback(debounce((key, quantity) => {
        setLineItems((lineItems) => {
            let clone = cloneDeep(lineItems);

            if (lineItems[key].increment_quantity_by) {
                const step = clone[key].increment_quantity_by;

                if (quantity > 0 && quantity < step) {
                    quantity = step;
                } else if ((quantity % step) < (step / 2)) {
                    quantity = quantity - (quantity % step);
                } else {
                    quantity = parseInt(quantity) + parseInt(step - (quantity % step));
                }

                clone[key].quantity = quantity.toString();
                clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);
                clone[key].discount_code = null;
            }

            return clone;
        });

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
            applied: false,
        }));

    }, 500), [lineItems]);

    const updateQuantity = useCallback((key, quantity) => {
        setCalculated(false);

        let clone = cloneDeep(lineItems);
        quantity = Number(quantity);

        if (isNaN(quantity) || quantity < 0) {
            quantity = 0;
        }

        const item = clone[key];
        const breakPrice = helpers.getPriceBreak(item.price_breaks, quantity);

        item.quantity = quantity.toString();
        item.price.original = item.price.unit;

        if (breakPrice !== null) {
            item.price.discounted = breakPrice.toFixed(2);
            item.price.price_list = true;
            item.price.locked = true;
        } else {
            item.price.discounted = item.price.original;
            item.price.price_list = false;
            item.price.locked = false;
        }

        item.price.total = helpers.calculateTotalPrice(item).toFixed(2);

        setLineItems(clone);

        const totalPrice = clone.reduce((acc, item) => {
            return acc + parseFloat(item.price.total);
        }, 0.0);
        setTotalPrice(totalPrice);

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
        }));

        setIsPlaceOrderVisible(false);
        setIsCalculateVisible(true);

        debounceQuantityUpdate(key, quantity);
    }, [lineItems, location]);

    const calculateOrder = async () => {
        setCalculating(true);
        try {
            const response = await placeDraftOrder(true);

            setTotalPrice(response?.totalPrice || 0);

            const calculatedLineItemsById = response.lineItems.reduce((carry, item) => ({
                ...carry,
                [item.variantId]: item,
            }), {});

            const updatedLineItems = lineItems.map((item) => {
                if (!calculatedLineItemsById[item.id]) {
                    return item;
                }

                const calculatedItem = calculatedLineItemsById[item.id];

                if (!calculatedItem.discountedPrice) {
                    return item;
                }

                const discountedPrice = parseFloat(calculatedItem.discountedPrice).toFixed(2);

                return {
                    ...item,
                    price: {
                        ...item.price,
                        discounted: discountedPrice,
                        price_list: true,
                        locked: true,
                        total: helpers.calculateTotalPrice(item).toFixed(2),
                    }
                }
            });

            setLineItems(updatedLineItems);

            setDiscountCode(state => ({
                ...state,
                loading: false,
            }));

            setIsCalculateVisible(false);
            setIsPlaceOrderVisible(true);
            setCalculated(true);
        } catch (error) {
            console.error('Error calculating order:', error);
            setDiscountCode(state => ({
                ...state,
                loading: false,
            }));
            setIsPlaceOrderVisible(false);
            setIsCalculateVisible(true);
        } finally {
            setCalculating(false);
        }
    };

    const updateManualPrice = useCallback((key, price) => {
        setCalculated(false);

        const clone = cloneDeep(lineItems);
        const item = clone[key];

        const quantity = Number(item.quantity);

        if (price === '' || price === null) {
            item.price.manual = null;

            const breakPrice = helpers.getPriceBreak(item.price_breaks, quantity);

            if (breakPrice !== null) {
                item.price.discounted = breakPrice.toFixed(2);
                item.price.price_list = true;
                item.price.locked = true;
            } else {
                item.price.discounted = item.price.original;
                item.price.price_list = false;
                item.price.locked = false;
            }

            item.price.total = helpers.calculateTotalPrice(item).toFixed(2);
        } else {
            item.price.manual = price;
            item.price.locked = true;

            const parsed = Number(price);
            if (!isNaN(parsed)) {
                item.price.total = (parsed * quantity).toFixed(2);
            }
        }

        item.discount_code = null;

        setLineItems(clone);

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
        }));

        setIsPlaceOrderVisible(false);
        setIsCalculateVisible(true);
    }, [lineItems, location]);


    // Getting the customer from the router parameter.
    useEffect(() => {
        getSessionToken(app).then(sessionToken => {
            const decodedToken = jwtDecode(sessionToken);

            if (decodedToken['sub']) {
                const id = decodedToken['sub'];
                setAdminUserId(id);
            }
        });
        Promise
            .all([
                Api.getCustomer(customerid).then(item => item.data),
                Api.getLocation(locationId).then(item => item.data),
                Api.getShippingMethods().then(results => results.data),
                Api.getCollectionsMethod().then(results => results.data),
            ])
            .then(([customerData, locationData, shippingMethodsData, collectionsData]) => {
                setCustomer(customerData.data);
                setLocation(locationData.data);
                setAdditionalInfo({
                    ...additionalInfo,
                    customer_notes: locationData.data.notes,
                });
                setShippingMethods(filterShippingMethods(locationData, shippingMethodsData));
                setCollections(collectionsData.data)
                if (customerData.data.shipping_method && customerData.data.shipping_method.name) {
                    setShippingMethod({
                        name: customerData.data.shipping_method?.name ?? '',
                        price: customerData.data.shipping_method?.price ?? '0',
                        key: customerData.data.shipping_method?.shopify_rate_id ?? customerData.data.shipping_method?.key //fallback for old values
                    });
                } else {
                    setShippingMethod({
                        name: 'Free Shipping',
                        price: 0,
                        key: 'free'
                    });
                }
                if (customerData?.data?.billing_address?.deleted_at) {
                    setDeletedBillingAddress(true);
                    setErrors(errors => ({
                        ...errors,
                        billing_address: 'Assigned billing address has been deleted. Please select a different one in customer details.',
                    }));
                }
            })
    }, []);

    const handleShippingMethodChange = useCallback((key) => {
        if (!key || key.length == 0) {
            setShippingMethod({
                name: '',
                price: '',
                key: '',
            });
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            setShippingMethod({
                name: '',
                price: '',
                key: '',
            });
            return;
        }

        setShippingMethod({
            name: method.name,
            price: method.price,
            key: method.key,
        });
    }, [shippingMethod, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        setShippingMethod({
            ...shippingMethod,
            [id]: value,
        });
    }, [shippingMethod]);

    const searchProducts = async () => {
        setSearching(true);
        setErrMessage(null)

        try {
            const { data } = await Api.getProducts(
                searchInput,
                locationId,
                includeDrafts,
                includeArchived,
                selectedCollections
            )

            const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

            setHasMoreResults(data.meta.last_page > 1);

            const updatedLineItems = data.data.filter(item => !selectedLineItems.some(({id}) => id == item.shopify_variant_id))
                .map(variant => ({
                    id: variant.shopify_variant_id,
                    shopify_product_id: variant.shopify_product_id,
                    quantity: "0",
                    title: `${variant.sku} - ${variant.product_title}`,
                    price: {
                        unit: variant.price,
                        discounted: variant.price_list
                            ? variant.price
                            : (parseFloat(variant.price) - (parseFloat(variant.price) * (customer?.discount ?? 0) / 100))
                                .toFixed(2),
                        total: 0,
                        manual: null,
                        locked: variant.price_list,
                        price_list: variant.price_list,
                        original: variant.original_price,
                    },
                    image_url: variant.image_url,
                    inventory_quantity: variant.inventory_quantity,
                    options: variant.options,
                    increment_quantity_by: variant.increment_quantity_by,
                    discount_code: null,
                    price_breaks: variant.price_breaks ?? {},
                    body_html: variant.body_html,
                })).concat(selectedLineItems);

            setLineItems(updatedLineItems);
            setSearching(false);
        } catch (error) {
            if (error.response?.status === 422) {
                setErrMessage(error.response.data.message)
            } else {
                setErrMessage('Something went wrong')
            }

            setSearching(false)
        }
    }

    const placeDraftOrder = async (preview = false) => {
        const submitErrors = {};
        const variants = lineItems
            .filter(item => item.quantity > 0)
            .flatMap(item => {
                const quantity = parseInt(item.quantity, 10);

                if (!item.price.discount || item.price.discount.quantity === 0) {
                    return [item];
                }

                if (item.price.discount.quantity === -1 || item.price.discount.quantity == quantity) {
                    return [{
                        ...item,
                        custom_attributes: [
                            {key: '_discount_code', value: item.price.discount.discountCodes.join(',')},
                        ],
                    }];
                }

                return [
                    {
                        ...item,
                        quantity: `${item.price.discount.quantity}`,
                        custom_attributes: [
                            {key: '_discount_code', value: item.price.discount.discountCodes.join(',')},
                        ],
                    },
                    {
                        ...item,
                        price: {
                            ...item.price,
                            discount: null,
                        },
                        discount_code: null,
                        quantity: `${quantity - item.price.discount.quantity}`,
                    }
                ].map(item => ({
                    ...item,
                    price: {
                        ...item.price,
                        total: helpers.calculateTotalPrice(item),
                    },
                }));
            })
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                applied_discount: helpers.getDiscountDetails(item),
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                discounted_price: (
                    (item.price.manual !== null && item.price.manual !== '')
                        ? parseFloat(item.price.manual)
                        : parseFloat(item.price.unit)
                ) * parseInt(item.quantity, 10),
                original_price: parseFloat(item.price.original),
            }));

        const draftOrderData = {
            variants,
            location_id: location.id,
            total_price: lineItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.total) - parseFloat(item.discount_code?.total || '0');
            }, 0.0),
            placed_by: additionalInfo.placed_by,
            account_number: customer?.account_number || null,
            po_number: additionalInfo.po_number,
            salesperson_number: additionalInfo.salesperson_number,
            customer_notes: location.notes,
            order_notes: additionalInfo.order_notes,
            billing_address: helpers.formatLocationAddress(location?.billing ?? customer?.default_location?.billing, customer, location?.phone, customer?.billing_address),
            shipping_address: helpers.formatLocationAddress(location?.shipping, customer, location?.phone),
            shipping_method: shippingMethod,
            user_id: adminUserId,
            discount_codes: discountCode.applied ? [discountCode.code] : [],
            preview: preview,
        };

        try {
            let {data} = await Api.placeDraftOrder(draftOrderData);

            if (!preview) {
                Api.updateLocationAfterOrder(location.id, {
                    notes: additionalInfo.customer_notes,
                    contact: additionalInfo.placed_by,
                });
            }

            return data.data;

        } catch (error) {
            if (error.response.status == 422) {
                Object.keys(error.response.data.errors).forEach(key => {
                    submitErrors[key] = error.response.data.errors[key][0];
                })
                setErrors(submitErrors);
            } else {
                setCreating(false);
                throw error;
            }
        }

        return null;
    }

    const placeAndReview = async () => {
        setCreating(true);

        const draft = await placeDraftOrder();

        if (draft) {
            setTimeout(() => {
                const id = `${draft.id}`.replace(/^.*\/(\d+)$/, '$1');
                redirect.dispatch(Redirect.Action.ADMIN_PATH, `/draft_orders/${id}`);
                setCreating(false);
            });

            return;
        }

        setCreating(false);
    }

    const changeAdditionalInfo = useCallback((value, id) => {
        setAdditionalInfo({
            ...additionalInfo,
            [id]: value,
        });
    }, [additionalInfo]);

    return (
        <Frame>
            <Page title={`Place Order`}>
                <Layout>
                    <CustomerOnHold customer={customer}/>

                    <Layout.Section>
                        <LegacyCard title={`Customer Account`} sectioned>
                            <DescriptionList items={[
                                {term: 'Account Number', description: customer?.account_number},
                                {
                                    term: 'Email',
                                    description: location?.shopify_customer_email || location?.tracking_email || customer?.email
                                },
                                {term: 'Phone', description: location?.phone_number || customer?.phone_number},
                                {
                                    term: 'Name',
                                    description: `${customer?.additional_data.first_name} ${customer?.additional_data.last_name}`
                                },
                                {term: 'Discount', description: `${location?.discount || 0}%`},
                                {term: 'Shipping', description: <AddressDetails address={location?.shipping}/>},
                                {
                                    term: 'Assigned Billing',
                                    description: <BillingDetails address={customer?.billing_address}/>
                                },
                                {term: "Payment Terms", description: customer?.payment_terms_template?.name ?? "None"},
                            ]}/>
                            <LegacyCard.Section>
                                <FormLayout>
                                    <Select label="Shipping Method"
                                            requiredIndicator={true}
                                            options={[
                                                {label: '--Select Shipping Method--', value: ''},
                                                ...(shippingMethods.map((method) => {
                                                    return {
                                                        label: method.label,
                                                        value: method.key + ''
                                                    }
                                                }))
                                            ]}
                                            onChange={handleShippingMethodChange}
                                            value={shippingMethod?.key + ''}
                                            id="shipping_method"
                                    />

                                    {shippingMethod?.key == 'custom' && (
                                        <LegacyStack distribution={"fillEvenly"}>
                                            <TextField requiredIndicator={true} label="Shipping Method Name" id="name"
                                                       value={shippingMethod.name}
                                                       onChange={changeCustomShippingMethod}/>
                                            <TextField requiredIndicator={true} label="Shipping Method Price" id="price"
                                                       type={"number"} min={0} step={1.0}
                                                       value={shippingMethod.price.toString()}
                                                       onChange={changeCustomShippingMethod}/>
                                        </LegacyStack>
                                    )}

                                    <TextField multiline={3} label="Customer Notes"
                                               value={additionalInfo.customer_notes} id="customer_notes"
                                               onChange={changeAdditionalInfo}/>
                                    <TextField requiredIndicator={true} label="Salesperson Number"
                                               id="salesperson_number" value={additionalInfo.salesperson_number}
                                               onChange={changeAdditionalInfo}/>
                                    <TextField requiredIndicator={true}
                                               label="Who is the person who is placing this order for the customer?"
                                               id="placed_by" value={additionalInfo.placed_by}
                                               onChange={changeAdditionalInfo}/>
                                    <TextField label="PO Number" id="po_number" value={additionalInfo.po_number}
                                               onChange={changeAdditionalInfo}/>
                                </FormLayout>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard title="Line Items">
                            <LegacyCard.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                        <MultipleSelect
                                            label="Select Tags to filter"
                                            initialOptions={collections.map(collection => {
                                                return {label: collection.handle, value: collection.id}
                                            })}
                                            onUpdate={(value) => {
                                                setSelectedCollections(value)
                                            }}
                                            selectedOptions={selectedCollections}
                                        />
                                        <TextField label="Search" value={searchInput} onChange={setSearchInput}/>
                                        <Checkbox label="Include Drafts" checked={includeDrafts}
                                                  onChange={setIncludeDrafts}/>

                                        <Checkbox label="Include Archived" checked={includeArchived}
                                                  onChange={setIncludeArchived}/>
                                        <Button submit loading={isSearching} variant="primary">Search</Button>
                                        {errMessage && (
                                            <Badge tone='attention'>{errMessage}</Badge>
                                        )}
                                    </FormLayout>
                                </Form>
                            </LegacyCard.Section>
                            {lineItems.length > 0 && (
                                <>
                                    <LegacyCard.Section title="Line Items">
                                        <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                            <thead>
                                            <tr>
                                                <th className="Polaris-DataTable__Cell" colSpan="2"
                                                    style={{textAlign: 'left'}}>Item
                                                </th>
                                                <th className="Polaris-DataTable__Cell"
                                                    style={{textAlign: 'left',}}>Stock
                                                </th>
                                                <th className="Polaris-DataTable__Cell"
                                                    style={{textAlign: 'left', width: '120px'}}>Quantity
                                                </th>
                                                <th className="Polaris-DataTable__Cell"
                                                    style={{textAlign: 'left', width: '200px'}}>Unit Price
                                                </th>
                                                <th className="Polaris-DataTable__Cell"
                                                    style={{textAlign: 'left', width: '80px'}}>Final Price
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {lineItems.map((line_item, key) => {
                                                return (
                                                    <tr key={line_item.id}>
                                                        <td className="Polaris-DataTable__Cell">
                                                            {line_item.body_html && <Tooltip width="wide"
                                                                                             content={parse(line_item.body_html)}>
                                                                <Thumbnail source={line_item.image_url}/>
                                                            </Tooltip> || <Thumbnail source={line_item.image_url}/>}
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell"
                                                            style={{whiteSpace: 'normal'}}>
                                                            <div>
                                                                {line_item.title}
                                                                {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                            </div>

                                                            {parseFloat(line_item.price?.discount?.total || '0') && (
                                                                <Text variant="bodyMd" as="span" tone="subdued">
                                                                    <DiscountFootnote
                                                                        discount={line_item.price?.discount}/>
                                                                </Text>
                                                            ) || null}
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Text variant="headingMd"
                                                                  as="h2">{line_item.inventory_quantity}</Text>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <TextField value={line_item.quantity}
                                                                       min={0}
                                                                       step={line_item.increment_quantity_by ?? 1}
                                                                       type="number"
                                                                       onChange={quantity => updateQuantity(key, quantity)}
                                                            />
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <FinalPrice lineItem={line_item}>
                                                                <TextField type="number"
                                                                           prefix="$"
                                                                           value={(line_item.price.manual === null) ? line_item.price.discounted : line_item.price.manual}
                                                                           min="0"
                                                                           max={line_item.price.unit}
                                                                           onChange={price => updateManualPrice(key, price)}/>
                                                            </FinalPrice>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Text variant="headingMd"
                                                                  as="h2">${(parseFloat(line_item.price.total) - parseFloat(line_item?.price?.discount?.total ?? 0)).toFixed(2)}</Text>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                            <tfoot>
                                            <tr>
                                                <td className="Polaris-DataTable__Cell" colSpan="4">
                                                    <Text variant="headingMd" as="h2">
                                                        {lineItems.reduce((acc, item) => acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10)), 0.0) - totalPrice > 0
                                                            ? "Total Before Discounts"
                                                            : "Total"}
                                                    </Text>
                                                </td>
                                                <td className="Polaris-DataTable__Cell">
                                                    <Text variant="headingMd" as="h2">
                                                        {`$${lineItems.reduce((acc, item) => {
                                                            return acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10));
                                                        }, 0.0).toFixed(2)}`}
                                                    </Text>
                                                </td>
                                            </tr>

                                            {lineItems.reduce((acc, item) => acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10)), 0.0) - totalPrice > 0 && (
                                                <>
                                                    <tr>
                                                        <td className="Polaris-DataTable__Cell" colSpan="4">
                                                            <Text variant="headingMd" as="h2">Discount</Text>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Text variant="headingMd" as="h2">
                                                                {`-$${Math.max(0, (lineItems.reduce((acc, item) => {
                                                                    return acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10));
                                                                }, 0.0) - totalPrice)).toFixed(2)}`}
                                                            </Text>
                                                        </td>
                                                    </tr>

                                                    <tr>
                                                        <td className="Polaris-DataTable__Cell" colSpan="4">
                                                            <Text variant="headingMd" as="h2">Total After
                                                                Discounts</Text>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Text variant="headingMd" as="h2">
                                                                {`$${parseFloat(totalPrice).toFixed(2)}`}
                                                            </Text>
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                            </tfoot>
                                        </table>
                                    </LegacyCard.Section>
                                </>
                            )}
                            {hasMoreResults && (
                                <LegacyCard.Section>
                                    <Banner tone="info">
                                        <p>There are more matching products. Please narrow down the search query.</p>
                                    </Banner>
                                </LegacyCard.Section>
                            )}
                        </LegacyCard>
                    </Layout.Section>
                    {lineItems.length > 0 && (<>
                        <Layout.Section>
                            <LegacyCard sectioned title="Discount Code">
                                <FormLayout>
                                    <LegacyStack>
                                        <TextField
                                            value={discountCode.code}
                                            onChange={(code) => setDiscountCode(state => ({
                                                ...state,
                                                code,
                                                requires_refresh: true
                                            }))}
                                            disabled={discountCode.applied}
                                        />
                                        {discountCode.applied ? (
                                            <Button
                                                onClick={removeDiscountCode}
                                                loading={discountCode.loading}
                                                variant="destructive"
                                            >
                                                Remove
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={applyDiscountCode}
                                                loading={discountCode.loading}
                                                variant="primary"
                                                disabled={!discountCode.code}
                                            >
                                                Apply
                                            </Button>
                                        )}
                                    </LegacyStack>
                                </FormLayout>
                            </LegacyCard>
                        </Layout.Section>
                    </>)}
                    <Layout.Section>
                        <LegacyCard sectioned title="Additional Information">
                            <FormLayout>
                                <TextField multiline={3} label="Order Notes" value={additionalInfo.order_notes}
                                           id="order_notes" onChange={changeAdditionalInfo}/>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.Section>
                    {customer?.cards == null && (
                        <Layout.Section>
                            <Banner
                                title="No credit card added to this customer"
                                tone="warning"
                            >
                                <TextContainer>
                                    <p>The customer you are placing an order for doesn't have a credit card added to his
                                        profile. Please add a credit card first.</p>
                                </TextContainer>
                            </Banner>
                        </Layout.Section>
                    )}
                    {Object.keys(errors).length > 0 && (
                        <Layout.Section>
                            <Banner
                                title="There were some errors creating the order."
                                tone="critical"
                            >
                                <List>
                                    {Object.keys(errors).map(key =>
                                        <List.Item><strong>{key.replace('_', ' ')}</strong>: {errors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        </Layout.Section>
                    )}
                    {discountCode.requires_refresh && discountCode.code && (
                        <Layout.Section>
                            <Banner tone="critical">
                                <p>The discount code requires a refresh. Please re-apply the discount before placing the
                                    order.</p>
                            </Banner>
                        </Layout.Section>
                    )}
                    <CustomerOnHold customer={customer}/>
                </Layout>

                <PageActions
                    primaryAction={
                        isCalculateVisible
                            ? {
                                content: 'Calculate',
                                onAction: calculateOrder,
                                loading: calculating,
                            }
                            : isPlaceOrderVisible
                                ? {
                                    content: 'Place Order and Review',
                                    onAction: placeAndReview,
                                    loading: creating,
                                }
                                : undefined
                    }
                    secondaryActions={[
                        {
                            content: 'Cancel order',
                            destructive: true,
                            onAction: () => {
                                if (confirm("Are you sure you want to cancel?")) {
                                    history.goBack();
                                }
                            }
                        },
                    ]}
                />
            </Page>
        </Frame>
    );
}

export default Orders;
